import {cloneDeep} from 'lodash';
import {Component, Input, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Toaster, Type} from '../../toaster/Toaster';
import {PortfolioType} from '../../model/portfolio-type.model';
import {PreferencesService} from '../../service/preferences.service';
import {PortfolioPreferences, PreferredCategories} from '../../model/portfolio-preferences.model';
import {
    PortfolioPreferencesConfirmationPopUpComponent,
    PortfolioPreferencesConfirmationPopUpData
} from './pop-up/portfolio-preferences-confirmation-pop-up.component';
import {PortfolioPreferencesPickerComponent} from '../../portfolio-preferences-picker/portfolio-preferences-picker.component';
import * as moment from 'moment';

@Component({
    selector: 'app-portfolio-detail-preferences',
    templateUrl: './portfolio-detail-preferences.component.html'
})
export class PortfolioDetailPreferencesComponent {
    public shouldShowLoader = false;
    public portfolioPreferences: PortfolioPreferences;
    @Input() public readonly portfolioType: PortfolioType;
    @Input() public portfolioRemainingAmount: number;
    @Input() public portfolioCreationDate: string;
    @ViewChild(PortfolioPreferencesPickerComponent)
    public portfolioPreferencesPickerComponent: PortfolioPreferencesPickerComponent;
    private readonly preferencesService: PreferencesService;
    private readonly matDialog: MatDialog;

    constructor(preferencesService: PreferencesService, matDialog: MatDialog) {
        this.preferencesService = preferencesService;
        this.matDialog = matDialog;
    }

    private _portfolioId: string;

    get portfolioId(): string {
        return this._portfolioId;
    }

    @Input() set portfolioId(value: string) {
        this._portfolioId = value;
        this.onPortfolioIdReceived(value);
    }

    public async onPortfolioIdReceived(portfolioId: string) {
        await this.fetchPortfolioPreferences(portfolioId);
    }

    async fetchPortfolioPreferences(portfolioId: string): Promise<void> {
        this.portfolioPreferences = await this.preferencesService.getPortfolioPreferences(portfolioId);
    }

    async onConfirmationReceived(preferredCategories: PreferredCategories[]): Promise<void> {
        this.showLoader();

        const originalPreferences = cloneDeep(this.portfolioPreferences);
        try {
            this.portfolioPreferences.preferredCategories = preferredCategories;
            await this.preferencesService.savePortfolioPreferences(this.portfolioPreferences);

            Toaster.toast('Your preferences have been changed for this portfolio. ' +
                'This change will be applicable to for any remaining amount in this portfolio.', Type.Success);
        } catch (error) {
            this.portfolioPreferences = originalPreferences;
            Toaster.toast('Something went wrong while saving your preferences. Please try again.', Type.Error);
        } finally {
            this.hideLoader();
        }
    }

    showLoader(): void {
        this.shouldShowLoader = true;
    }

    hideLoader(): void {
        this.shouldShowLoader = false;
    }

    shouldShowMainComponent(portfolioType: PortfolioType) {
        return ![PortfolioType.Reinvest, PortfolioType.ManualInvest].includes(portfolioType);
    }

    isEditable(portfolioRemainingAmount: number, portfolioCreationDate: string) {
        return portfolioRemainingAmount > 0 ||
            moment(portfolioCreationDate).isSameOrAfter(moment().subtract(3, 'months').startOf('second'));
    }

    async onSavePreferencesClicked(preferredCategories: PreferredCategories[]) {
        this.showLoader();

        const dialogRef = this.matDialog.open(PortfolioPreferencesConfirmationPopUpComponent, {
            data: <PortfolioPreferencesConfirmationPopUpData>{
                preferredCategories: preferredCategories,
                negativeButtonText: 'CANCEL',
                positiveButtonText: 'CONFIRM',
                title: 'Confirm your preference change'
            }
        });

        const result = await dialogRef.afterClosed().toPromise();
        this.hideLoader();

        if (result) {
            this.onConfirmationReceived(preferredCategories);
        } else {
            this.portfolioPreferencesPickerComponent.cancel();
        }
    }
}